import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Lock, Share2, FolderOpen, Key, Copy, Check, Bookmark, BookmarkCheck, ArrowBigUp, ArrowBigDown } from 'lucide-react';
import { WhatsappShareButton, TelegramShareButton, TwitterShareButton, RedditShareButton } from 'react-share';
import { FaWhatsapp, FaTelegram, FaXTwitter, FaReddit } from 'react-icons/fa6';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../config';
import { getDisplayAuthor, getAuthorStyle, getAuthorAvatarColor, shouldShowPremiumBadge, getAuthorContainerClass } from '../utils/authorDisplay';
import PremiumBadge from './PremiumBadge';

const EMOJI_REACTIONS = ['👍', '❤️', '🔥', '😮', '😢'];

const EnhancedPostCard = ({ post, onUnlock, onVote, onSave, onUnsave, user, onReactionUpdate }) => {
  const [showShare, setShowShare] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const [copied, setCopied] = useState(false);
  const [localSaved, setLocalSaved] = useState(post.is_saved || false);
  const [localVote, setLocalVote] = useState(post.user_vote || null);
  const [localUpvotes, setLocalUpvotes] = useState(post.upvotes || 0);
  const [localDownvotes, setLocalDownvotes] = useState(post.downvotes || 0);
  const [localReactions, setLocalReactions] = useState(post.reactions || {});
  const [userReaction, setUserReaction] = useState(post.user_reaction || null);
  const [voteAnimation, setVoteAnimation] = useState({ upvote: false, downvote: false });
  
  // Get display author and styling
  const displayAuthor = getDisplayAuthor(post);
  const authorStyle = getAuthorStyle(post);
  const avatarColor = getAuthorAvatarColor(post);
  
  const shareUrl = window.location.origin;
  const shareText = `Check out this post on Ebonythroatqueens`;

  const handleKeyClick = () => {
    const isAdminOrPremium = user && (
      user.role === 'admin' || 
      user.role === 'super_admin' || 
      user.is_premium
    );

    if (isAdminOrPremium) {
      if (post.premium_key_link) {
        window.open(post.premium_key_link, '_blank');
      } else {
        toast.error('No key link available for this post');
      }
    } else {
      onUnlock(post);
    }
  };

  const copyKeyToClipboard = async () => {
    if (post.premium_key_link) {
      try {
        await navigator.clipboard.writeText(post.premium_key_link);
        setCopied(true);
        toast.success('Key link copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        toast.error('Failed to copy');
      }
    }
  };

  const handleVote = async (voteType) => {
    // Trigger animation
    setVoteAnimation(prev => ({ ...prev, [voteType]: true }));
    setTimeout(() => {
      setVoteAnimation(prev => ({ ...prev, [voteType]: false }));
    }, 200);

    try {
      if (localVote === voteType) {
        setLocalVote(null);
        if (voteType === 'upvote') {
          setLocalUpvotes(prev => Math.max(0, prev - 1));
        } else {
          setLocalDownvotes(prev => Math.max(0, prev - 1));
        }
        await onVote(post.post_id, null);
      } else {
        const oldVote = localVote;
        setLocalVote(voteType);
        
        if (oldVote === 'upvote') {
          setLocalUpvotes(prev => Math.max(0, prev - 1));
          setLocalDownvotes(prev => prev + 1);
        } else if (oldVote === 'downvote') {
          setLocalDownvotes(prev => Math.max(0, prev - 1));
          setLocalUpvotes(prev => prev + 1);
        } else {
          if (voteType === 'upvote') {
            setLocalUpvotes(prev => prev + 1);
          } else {
            setLocalDownvotes(prev => prev + 1);
          }
        }
        
        await onVote(post.post_id, voteType);
      }
    } catch (error) {
      toast.error('Failed to vote');
      setLocalVote(post.user_vote || null);
      setLocalUpvotes(post.upvotes || 0);
      setLocalDownvotes(post.downvotes || 0);
    }
  };

  const handleReaction = async (emoji) => {
    if (post.is_locked) {
      toast.error('Unlock post to react');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      if (userReaction === emoji) {
        // Remove reaction
        await axios.delete(`${API_URL}/posts/${post.post_id}/react`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setLocalReactions(prev => ({
          ...prev,
          [emoji]: Math.max(0, (prev[emoji] || 0) - 1)
        }));
        setUserReaction(null);
      } else {
        // Add or change reaction
        await axios.post(`${API_URL}/posts/${post.post_id}/react`, 
          { emoji },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setLocalReactions(prev => {
          const newReactions = { ...prev };
          if (userReaction) {
            newReactions[userReaction] = Math.max(0, (newReactions[userReaction] || 0) - 1);
          }
          newReactions[emoji] = (newReactions[emoji] || 0) + 1;
          return newReactions;
        });
        setUserReaction(emoji);
      }
      
      setShowReactions(false);
    } catch (error) {
      toast.error('Failed to react');
    }
  };

  const handleSave = async () => {
    try {
      if (localSaved) {
        setLocalSaved(false);
        await onUnsave(post.post_id);
        toast.success('Post removed from saved');
      } else {
        setLocalSaved(true);
        await onSave(post.post_id);
        toast.success('Post saved');
      }
    } catch (error) {
      toast.error('Failed to save post');
      setLocalSaved(post.is_saved || false);
    }
  };

  const totalReactions = Object.values(localReactions).reduce((sum, count) => sum + count, 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -3 }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.2 }}
      className="post-card rounded-xl overflow-hidden"
      style={{ background: '#121212', border: '1px solid #2A2A2A' }}
      data-testid={`post-card-${post.post_id}`}
    >
      {/* Header */}
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center" 
            style={{ background: avatarColor }}
          >
            <span className="text-white font-bold text-sm">{displayAuthor[0]}</span>
          </div>
          <div>
            <div className={getAuthorContainerClass(post)}>
              <span 
                className="text-sm font-semibold" 
                style={{
                  color: authorStyle.color,
                  fontWeight: '600',
                  textTransform: authorStyle.textTransform,
                  fontFamily: authorStyle.fontFamily,
                  letterSpacing: authorStyle.letterSpacing,
                  textShadow: authorStyle.textShadow
                }}
                data-testid={`post-author-${post.post_id}`}
              >
                {displayAuthor}
              </span>
              {shouldShowPremiumBadge(post) && <PremiumBadge size={14} />}
            </div>
            <p className="text-gray-500 text-xs">
              {new Date(post.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        
        <span
          className="text-xs font-bold flex items-center gap-1"
          style={{
            color: post.post_type === 'premium' ? '#FFD700' : '#A1A1AA'
          }}
          data-testid={`post-type-${post.post_id}`}
        >
          {post.post_type === 'premium' && <span style={{ fontSize: '16px' }}>👑</span>} {post.post_type === 'premium' ? 'PREMIUM' : 'FREE'}
        </span>
      </div>

      {/* Content */}
      <div className="relative">
        {post.image_url && (
          <img
            src={post.image_url}
            alt={post.title || 'Post image'}
            className="w-full h-auto object-cover"
            style={{ maxHeight: '400px' }}
            data-testid={`post-image-${post.post_id}`}
          />
        )}
        
        <div className="p-4">
          {post.title && (
            <h2 
              className="text-white font-bold text-2xl mb-3" 
              style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.01em' }}
              data-testid={`post-title-${post.post_id}`}
            >
              {post.title}
            </h2>
          )}
          <p 
            className="text-gray-300 text-base mb-4 leading-relaxed" 
            style={{ fontFamily: 'Inter, sans-serif', lineHeight: '1.7' }}
            data-testid={`post-text-${post.post_id}`}
          >
            {post.text}
          </p>
          
          {/* Action Buttons */}
          <div className="flex gap-3">
            {post.mega_folder_link && (
              <motion.a
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                href={post.mega_folder_link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-bold"
                style={{ background: '#2A2A2A', color: 'white' }}
                data-testid={`mega-folder-button-${post.post_id}`}
              >
                <FolderOpen className="w-4 h-4" />
                {post.feed_id === 'eliteebony' ? 'Get PPV Pack' : 'MEGA'}
              </motion.a>
            )}
            
            {(post.linkvertise_url || post.premium_key_link) && (
              <div className="flex-1 flex gap-2">
                <motion.button
                  whileHover={{ scale: 1.02, boxShadow: '0 0 25px rgba(238, 28, 78, 0.6)' }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleKeyClick}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-bold"
                  style={{ 
                    background: '#EE1C4E', 
                    color: 'white',
                    boxShadow: '0 0 20px rgba(238, 28, 78, 0.4)'
                  }}
                  data-testid={`key-button-${post.post_id}`}
                >
                  {post.is_locked ? <Lock className="w-4 h-4" /> : <Key className="w-4 h-4" />}
                  <span>{post.is_locked ? 'Unlock' : 'Key'}</span>
                </motion.button>
                
                {user && (user.role === 'admin' || user.role === 'super_admin' || user.is_premium) && post.premium_key_link && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={copyKeyToClipboard}
                    className="px-3 py-3 rounded-lg text-sm font-bold"
                    style={{ background: copied ? '#00C853' : '#2A2A2A', color: 'white' }}
                    title="Copy key link"
                    data-testid={`copy-key-button-${post.post_id}`}
                  >
                    <motion.div
                      initial={false}
                      animate={{ scale: copied ? [1, 1.2, 1] : 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </motion.div>
                  </motion.button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Reactions, Voting, Save & Share */}
      <div className="p-4 border-t space-y-3" style={{ borderColor: '#2A2A2A' }}>
        {/* Emoji Reactions */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setShowReactions(!showReactions)}
              className="px-3 py-1.5 rounded-lg transition-all flex items-center gap-2"
              style={{
                background: userReaction ? '#1E1E1E' : 'transparent',
                border: '1px solid #2A2A2A',
                color: '#A1A1A1'
              }}
              disabled={post.is_locked}
              data-testid={`reactions-button-${post.post_id}`}
            >
              <span className="text-lg">{userReaction || '😊'}</span>
              {totalReactions > 0 && (
                <span className="text-xs font-semibold">{totalReactions}</span>
              )}
            </motion.button>
            
            <AnimatePresence>
              {showReactions && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                  className="absolute bottom-full left-0 mb-2 p-2 rounded-lg flex gap-2"
                  style={{ 
                    background: '#1A1A1A', 
                    border: '1px solid #2A2A2A',
                    zIndex: 20,
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
                  }}
                  data-testid={`reactions-menu-${post.post_id}`}
                >
                  {EMOJI_REACTIONS.map((emoji) => (
                    <motion.button
                      key={emoji}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      whileHover={{ scale: 1.3 }}
                      whileTap={{ scale: 0.9 }}
                      transition={{ type: "spring", stiffness: 500, damping: 25 }}
                      onClick={() => handleReaction(emoji)}
                      className="text-2xl p-2 rounded-lg hover:bg-white/5"
                      style={{
                        background: userReaction === emoji ? '#2A2A2A' : 'transparent'
                      }}
                      data-testid={`reaction-${emoji}-${post.post_id}`}
                    >
                      {emoji}
                    </motion.button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {/* Show individual reaction counts */}
          {Object.entries(localReactions).map(([emoji, count]) => count > 0 && (
            <motion.div
              key={emoji}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="px-2 py-1 rounded-lg flex items-center gap-1"
              style={{ background: '#1E1E1E', border: '1px solid #2A2A2A' }}
            >
              <span className="text-sm">{emoji}</span>
              <span className="text-xs font-semibold" style={{ color: '#A1A1A1' }}>{count}</span>
            </motion.div>
          ))}
        </div>
        
        <div className="flex items-center justify-between">
          {/* Voting */}
          <div className="flex items-center gap-2" data-testid={`voting-${post.post_id}`}>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              animate={{ 
                scale: voteAnimation.upvote ? 1.15 : 1,
                backgroundColor: voteAnimation.upvote 
                  ? (localVote === 'upvote' ? '#00E676' : '#00C853')
                  : (localVote === 'upvote' ? '#00C853' : 'transparent')
              }}
              transition={{ duration: 0.15 }}
              onClick={() => handleVote('upvote')}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg"
              style={{
                border: '1px solid #2A2A2A',
                color: localVote === 'upvote' ? 'white' : '#A1A1A1'
              }}
              data-testid={`upvote-button-${post.post_id}`}
              title="Upvote"
            >
              <ArrowBigUp className="w-5 h-5" fill={localVote === 'upvote' ? 'white' : 'none'} />
              <motion.span 
                className="text-sm font-semibold"
                animate={{ 
                  scale: voteAnimation.upvote ? 1.3 : 1,
                  y: voteAnimation.upvote ? -2 : 0
                }}
                transition={{ type: "spring", stiffness: 500, damping: 15 }}
              >
                {localUpvotes}
              </motion.span>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              animate={{ 
                scale: voteAnimation.downvote ? 1.15 : 1,
                backgroundColor: voteAnimation.downvote 
                  ? (localVote === 'downvote' ? '#FF1744' : '#EE1C4E')
                  : (localVote === 'downvote' ? '#EE1C4E' : 'transparent')
              }}
              transition={{ duration: 0.15 }}
              onClick={() => handleVote('downvote')}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg"
              style={{
                border: '1px solid #2A2A2A',
                color: localVote === 'downvote' ? 'white' : '#A1A1A1'
              }}
              data-testid={`downvote-button-${post.post_id}`}
              title="Downvote"
            >
              <ArrowBigDown className="w-5 h-5" fill={localVote === 'downvote' ? 'white' : 'none'} />
              <motion.span 
                className="text-sm font-semibold"
                animate={{ 
                  scale: voteAnimation.downvote ? 1.3 : 1,
                  y: voteAnimation.downvote ? 2 : 0
                }}
                transition={{ type: "spring", stiffness: 500, damping: 15 }}
              >
                {localDownvotes}
              </motion.span>
            </motion.button>
          </div>
          
          {/* Save & Share */}
          <div className="flex items-center gap-2">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              onClick={handleSave}
              className="p-2 rounded-lg transition-all hover:bg-white/5"
              data-testid={`save-button-${post.post_id}`}
              title={localSaved ? 'Unsave post' : 'Save post'}
            >
              {localSaved ? (
                <BookmarkCheck className="w-5 h-5 text-[#EE1C4E]" fill="#EE1C4E" />
              ) : (
                <Bookmark className="w-5 h-5 text-gray-400" />
              )}
            </motion.button>
            
            <div className="relative">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
                <Button
                  onClick={() => setShowShare(!showShare)}
                  variant="ghost"
                  size="sm"
                  className="text-gray-400 hover:text-white p-2"
                  data-testid={`share-button-${post.post_id}`}
                >
                  <Share2 className="w-5 h-5" />
                </Button>
              </motion.div>
              
              <AnimatePresence>
                {showShare && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.9 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 bottom-full mb-2 p-3 rounded-lg flex gap-3"
                    style={{ 
                      background: '#1A1A1A', 
                      border: '1px solid #2A2A2A',
                      zIndex: 10,
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
                    }}
                    data-testid={`share-menu-${post.post_id}`}
                  >
                    <WhatsappShareButton url={shareUrl} title={shareText}>
                      <motion.div whileHover={{ scale: 1.2 }} whileTap={{ scale: 0.9 }}>
                        <FaWhatsapp className="w-6 h-6 text-green-500 cursor-pointer" />
                      </motion.div>
                    </WhatsappShareButton>
                    
                    <TelegramShareButton url={shareUrl} title={shareText}>
                      <motion.div whileHover={{ scale: 1.2 }} whileTap={{ scale: 0.9 }}>
                        <FaTelegram className="w-6 h-6 text-blue-500 cursor-pointer" />
                      </motion.div>
                    </TelegramShareButton>
                    
                    <TwitterShareButton url={shareUrl} title={shareText}>
                      <motion.div whileHover={{ scale: 1.2 }} whileTap={{ scale: 0.9 }}>
                        <FaXTwitter className="w-6 h-6 text-white cursor-pointer" />
                      </motion.div>
                    </TwitterShareButton>
                    
                    <RedditShareButton url={shareUrl} title={shareText}>
                      <motion.div whileHover={{ scale: 1.2 }} whileTap={{ scale: 0.9 }}>
                        <FaReddit className="w-6 h-6 text-orange-500 cursor-pointer" />
                      </motion.div>
                    </RedditShareButton>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default EnhancedPostCard;
